
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const New2FAQ = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Questions fréquentes",
      subtitle: "Tout ce que vous devez savoir sur Legatia",
      faqs: [
        {
          question: "Comment fonctionne l'investissement ?",
          answer: "Votre contribution mensuelle est investie dans un portefeuille diversifié et éthique, géré selon les standards bancaires suisses. Nous privilégions des investissements durables avec un objectif de rendement moyen de 5% par an. Vous gardez un contrôle total et pouvez consulter les performances à tout moment."
        },
        {
          question: "Que se passe-t-il si je veux arrêter ou modifier ma contribution ?",
          answer: "Vous avez une flexibilité totale. Vous pouvez suspendre, réduire ou augmenter vos contributions à tout moment sans frais. Vos fonds déjà investis continuent de fructifier et votre capsule temporelle reste active."
        },
        {
          question: "Comment puis-je être sûr que mes messages seront délivrés ?",
          answer: "Nous utilisons plusieurs systèmes de sauvegarde redondants et des partenaires technologiques de confiance. De plus, vous recevez des confirmations régulières et pouvez à tout moment vérifier et modifier vos capsules. En cas de problème technique, notre équipe vous contacte directement."
        },
        {
          question: "Qu'arrive-t-il si Legatia disparaît ?",
          answer: "Vos fonds sont protégés dans des comptes séparés chez des banques suisses réglementées. En cas d'arrêt de nos services, tous les investissements et capsules sont transférés vers un service successeur ou directement aux bénéficiaires selon vos instructions."
        },
        {
          question: "Puis-je inviter d'autres membres de la famille ?",
          answer: "Absolument ! C'est même l'une de nos fonctionnalités phares. Vous pouvez inviter grands-parents, oncles, tantes, parrains/marraines à contribuer avec leurs propres messages et participations financières. Chaque capsule devient un projet familial collaboratif."
        },
        {
          question: "Y a-t-il des frais cachés ?",
          answer: "Non, nous pratiquons une transparence totale. Le prix affiché inclut tout : plateforme, gestion des investissements, stockage sécurisé, et support client. Les seuls frais variables sont liés aux performances d'investissement (success fees), clairement détaillés dans votre contrat."
        }
      ]
    },
    en: {
      title: "Frequently asked questions",
      subtitle: "Everything you need to know about Legatia",
      faqs: [
        {
          question: "How does the investment work?",
          answer: "Your monthly contribution is invested in a diversified and ethical portfolio, managed according to Swiss banking standards. We favor sustainable investments with an average return target of 5% per year. You maintain total control and can check performance at any time."
        },
        {
          question: "What happens if I want to stop or modify my contribution?",
          answer: "You have complete flexibility. You can suspend, reduce or increase your contributions at any time without fees. Your already invested funds continue to grow and your time capsule remains active."
        },
        {
          question: "How can I be sure my messages will be delivered?",
          answer: "We use multiple redundant backup systems and trusted technology partners. Additionally, you receive regular confirmations and can check and modify your capsules at any time. In case of technical issues, our team contacts you directly."
        },
        {
          question: "What happens if Legatia disappears?",
          answer: "Your funds are protected in separate accounts at regulated Swiss banks. In case our services stop, all investments and capsules are transferred to a successor service or directly to beneficiaries according to your instructions."
        },
        {
          question: "Can I invite other family members?",
          answer: "Absolutely! It's actually one of our flagship features. You can invite grandparents, uncles, aunts, godparents to contribute with their own messages and financial participation. Each capsule becomes a collaborative family project."
        },
        {
          question: "Are there hidden fees?",
          answer: "No, we practice total transparency. The displayed price includes everything: platform, investment management, secure storage, and customer support. The only variable fees are related to investment performance (success fees), clearly detailed in your contract."
        }
      ]
    },
    de: {
      title: "Häufig gestellte Fragen",
      subtitle: "Alles, was Sie über Legatia wissen müssen",
      faqs: [
        {
          question: "Wie funktioniert die Investition?",
          answer: "Ihr monatlicher Beitrag wird in ein diversifiziertes und ethisches Portfolio investiert, das nach Schweizer Bankenstandards verwaltet wird. Wir bevorzugen nachhaltige Investitionen mit einem durchschnittlichen Renditeziel von 5% pro Jahr. Sie behalten die volle Kontrolle und können die Performance jederzeit überprüfen."
        },
        {
          question: "Was passiert, wenn ich meinen Beitrag stoppen oder ändern möchte?",
          answer: "Sie haben vollständige Flexibilität. Sie können Ihre Beiträge jederzeit ohne Gebühren aussetzen, reduzieren oder erhöhen. Ihre bereits investierten Mittel wachsen weiter und Ihre Zeitkapsel bleibt aktiv."
        },
        {
          question: "Wie kann ich sicher sein, dass meine Nachrichten geliefert werden?",
          answer: "Wir verwenden mehrere redundante Backup-Systeme und vertrauenswürdige Technologiepartner. Zusätzlich erhalten Sie regelmäßige Bestätigungen und können Ihre Kapseln jederzeit überprüfen und ändern. Bei technischen Problemen kontaktiert Sie unser Team direkt."
        },
        {
          question: "Was passiert, wenn Legatia verschwindet?",
          answer: "Ihre Mittel sind in separaten Konten bei regulierten Schweizer Banken geschützt. Falls unsere Dienste eingestellt werden, werden alle Investitionen und Kapseln an einen Nachfolgedienst oder direkt an die Begünstigten gemäß Ihren Anweisungen übertragen."
        },
        {
          question: "Kann ich andere Familienmitglieder einladen?",
          answer: "Absolut! Es ist sogar eine unserer Hauptfunktionen. Sie können Großeltern, Onkel, Tanten, Paten einladen, mit ihren eigenen Nachrichten und finanzieller Beteiligung beizutragen. Jede Kapsel wird zu einem kollaborativen Familienprojekt."
        },
        {
          question: "Gibt es versteckte Gebühren?",
          answer: "Nein, wir praktizieren völlige Transparenz. Der angezeigte Preis beinhaltet alles: Plattform, Anlageverwaltung, sichere Speicherung und Kundensupport. Die einzigen variablen Gebühren beziehen sich auf die Anlageperformance (Erfolgsgebühren), die in Ihrem Vertrag klar aufgeführt sind."
        }
      ]
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content[language].title}
          </h2>
          <p className="text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {content[language].faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-gray-50 rounded-lg px-6 border-0"
              >
                <AccordionTrigger className="text-left text-lg font-semibold py-6 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default New2FAQ;
